import {IntegerParameter} from "parameters/AbstractParameter.js";
import {uuid} from 'uuidv4';
import ipfs from 'decentralized/Ipfs.js';
import Tone from 'tone';
import {setParameterMinMax} from 'actions/index.js';
import {publishPlanarSnapshot} from "messaging/messages.js";

/**
 * Enables snapshots to be taken
 */

export default superclass => class extends superclass  {
    snapshots = [];
    currentSnapshot = undefined;
    snapshotComponent = {
        id: uuid()
    };
    
    setupSnapshots() {
        this.currentSnapshotParameter = new IntegerParameter(
            "currentSnapshot",
            this,
            (o,i, automation, time) => {
                this.setting = true;
                if (i < 0) {
                    this.currentSnapshot = undefined;
                }
                return i >= 0 ? this._useSnapshot(this.snapshots[i], time) : -1;
            },
            {
                min: 0,
                max: 100,
                defaultValue: null,
                stepAutomation: true
            }
        );
        this.registerParameter(this.currentSnapshotParameter);
    }

    takeSnapshot(setToCurrent=false) {
        let snapshot = [];
        for (let parameter of this._parameters) {
            snapshot.push(
                {
                    parameter,
                    coordinates: parameter.getCoordinates(),
                    min: parameter.min,
                    max: parameter.max,
                });
        }
        this.snapshots = [... this.snapshots, snapshot];
        if (setToCurrent) {
            this.currentSnapshotParameter.set(this.snapshots.length - 1);
        }
    }

    deleteSnapshot(snapshot) {
        this.snapshots = this.snapshots.filter(s => s !== snapshot);
        this.currentSnapshot = undefined;
    }

    _useSnapshot(snapshot, time) {
        if (!snapshot) {
            return;
        }
        if (snapshot === this.currentSnapshot) {
            // we dont need to update...
            return;
        }


        let minMaxBulk = {};
        let bulk = {};
        let parameters = new Set(this._parameters);
        for (let {parameter, coordinates, min, max} of snapshot) {
            if (!parameters.has(parameter)) {
                // only parameters that are still contained in the plane effect can
                // be used in snapshots
                continue;
            }

            if (min !== undefined && max !== undefined) {
                parameter.min = min;
                parameter.max = max;
            }
            minMaxBulk[parameter.id] = {min, max};
            
            // first check if the parameter will change
            let _coordinates = this.groups.map(
                x => x.history[x.history.length - 1] && x.history[x.history.length - 1][parameter.id])
                .find(x => x);

            if (_coordinates && _coordinates.x === coordinates.x &&
                _coordinates.y === coordinates.y) {
                // parameter did not change so no need to waste CPU,
                // so we skip
                continue;
            } 
            
            let _time = Tone.Master.context.isOffline ?
                Tone.Master.context._currentTime  :
                Tone.Master.context.currentTime;
            let {id, value} = parameter.setCoordinates(
                coordinates.x, coordinates.y, _time + 0.001, this.rampTime / 1000.0, true);
            bulk[id] = value;
        }
        this.setEffectParameterValues(bulk);
        this.setParameterMinMax(minMaxBulk);

        // When user hits stop, it will now use this snapshot to reset the values
        this._clear();
        this.currentSnapshot = snapshot;

        for (let group of this.groups) {
            if (group.history && group.history[0]) {
                // find any overlapping history and replace the first thing with that so
                // that resets work
                for (let id in group.history[0]) {
                    let s = snapshot.find(({parameter}) => parameter.id === id);
                    if (s) {
                        group.history[0][id] = s.coordinates;
                    }
                }
            }
        }
        publishPlanarSnapshot(this.snapshots.indexOf(snapshot));
        this.setting = false;
    }

    setParameterMinMax(bulk) {
        this.track.sequencerEngine.dispatch(
            setParameterMinMax(bulk));
    }

    async dropPreset(preset) {
        let json = await ipfs.getJson(preset.contentHash);
        let instrumentJson = json.trackParameters.instrument;
        await this.track.instrument.fromJson(
            instrumentJson, true);
        this.takeSnapshot();
    }
}
